import { Injectable } from '@angular/core';
import { ActiveCartService } from '@cart/data-access';
import { Address, Addresses, DELIVERY } from '@checkout/const';
import { CheckoutActions, CheckoutApiService } from '@checkout/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DeliveryAddressService, ReceiptService } from '../services';

@Injectable()
export class SavedDeliveryAddressEffects {
  loadAddressList$: Observable<
    CheckoutActions.LoadSavedDeliveryAddressListSuccess | CheckoutActions.LoadSavedDeliveryAddressListFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_SAVED_DELIVERY_ADDRESS_LIST),
      switchMap(() =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(take(1)),
      ),
      filter(({ cartId }) => !!cartId),
      switchMap(({ cartId }) =>
        this.checkoutApiService.loadDeliveryAddressList(cartId).pipe(
          map((data: Addresses) => new CheckoutActions.LoadSavedDeliveryAddressListSuccess(data)),
          catchError((error) => of(new CheckoutActions.LoadSavedDeliveryAddressListFail(error))),
        ),
      ),
    ),
  );

  saveAddress$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.SAVE_DELIVERY_ADDRESS),
      map((action: CheckoutActions.SaveDeliveryAddress) => action.payload),
      switchMap((payload) =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(
          take(1),
          map(({ cartId }) => ({ cartId, address: payload })),
        ),
      ),
      filter(({ cartId }) => !!cartId),
      switchMap(({ cartId, address }: { cartId: string; address: Address }) => {
        return this.checkoutApiService.saveDeliveryAddress(cartId, address).pipe(
          tap((data) => this.deliveryAddressService.selectActiveAddressId(data.id)),
          map(() => new CheckoutActions.LoadSavedDeliveryAddressList()),
        );
      }),
    ),
  );

  editAddress$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.EDIT_SAVED_DELIVERY_ADDRESS),
      map((action: CheckoutActions.EditSavedDeliveryAddress) => action.payload),
      switchMap((payload) =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(
          take(1),
          map(({ cartId }) => ({ cartId, address: payload })),
        ),
      ),
      filter(({ cartId }) => !!cartId),
      switchMap(({ cartId, address }: { cartId: string; address: Address }) =>
        this.checkoutApiService.editDeliveryAddress(cartId, address).pipe(
          tap(() => this.deliveryAddressService.selectActiveAddressId(address.id)),
          map(() => new CheckoutActions.LoadSavedDeliveryAddressList()),
        ),
      ),
    ),
  );

  deleteAddress$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.DELETE_SAVED_DELIVERY_ADDRESS),
      map((action: CheckoutActions.DeleteSavedDeliveryAddress) => action.payload),
      switchMap((payload) =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(
          take(1),
          map(({ cartId }) => ({ cartId, addressId: payload })),
        ),
      ),
      filter(({ cartId }) => !!cartId),
      switchMap(({ cartId, addressId }: { cartId: string; addressId: string }) =>
        this.checkoutApiService
          .deleteDeliveryAddress(cartId, addressId)
          .pipe(map(() => new CheckoutActions.LoadSavedDeliveryAddressList())),
      ),
    ),
  );

  preselectAddress$: Observable<CheckoutActions.SelectActiveDeliveryAddress | CheckoutActions.ResetDeliveryFeatures> =
    createEffect(() =>
      combineLatest({
        addresses: this.actions$.pipe(
          ofType(CheckoutActions.LOAD_SAVED_DELIVERY_ADDRESS_LIST_SUCCESS),
          map((action: CheckoutActions.LoadSavedDeliveryAddressListSuccess) => action.payload),
          filter(({ addresses }, index) => Boolean(addresses[0]) || index > 0),
          map(({ addresses }) => addresses),
        ),
        activeReceiptType: this.receiptService.activeReceiptTypeId$,
      }).pipe(
        switchMap(({ addresses, activeReceiptType }) =>
          this.deliveryAddressService.activeAddressId$.pipe(
            take(1),
            map((activeAddressId) => ({ addresses, activeReceiptType, activeAddressId })),
          ),
        ),
        filter(({ activeReceiptType }) => activeReceiptType === DELIVERY),
        map(({ addresses, activeAddressId }) => {
          const address = addresses.find((addr) => addr.id === activeAddressId) || addresses[0];

          if (address) {
            return new CheckoutActions.SelectActiveDeliveryAddress(address.id);
          }

          return new CheckoutActions.ResetDeliveryFeatures();
        }),
      ),
    );

  reloadAddressListAfterSetReceiptType$: Observable<CheckoutActions.LoadSavedDeliveryAddressList> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.SET_ACTIVE_RECEIPT_TYPE),
      map((action: CheckoutActions.SetReceiptType) => action.payload),
      filter((type) => type === DELIVERY),
      map(() => new CheckoutActions.LoadSavedDeliveryAddressList()),
    ),
  );

  constructor(
    private actions$: Actions,
    private checkoutApiService: CheckoutApiService,
    private activeCartService: ActiveCartService,
    private receiptService: ReceiptService,
    private deliveryAddressService: DeliveryAddressService,
  ) {}
}
